import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert, ScrollView } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import ScreenWrapper from '../../Components/ScreenWrapper';
import { theme } from '../../constants/theme';
import { hp, wp } from '../../helpers/common';
import { createReport, checkIfAlreadyReported } from '../../services/reportService';
import { useAuth } from '../../Utils/AuthProvider';

interface RouteParams {
  postId: string;
}

const REPORT_REASONS = [
  'It’s spam',
  'it contains personal details of others.',
  'Post has been done by a man!',
  'This post contains fake/scammy/marketing content',
  'This post contains abusive/violent/pornographic content.',
  'Others',
];

export default function ReportPost() {
  const navigation = useNavigation<any>();
  const route = useRoute();
  const { postId } = route.params as RouteParams;
  const { user } = useAuth();

  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [alreadyReported, setAlreadyReported] = useState(false);

  useEffect(() => {
    checkAlreadyReported();
  }, []);

  const checkAlreadyReported = async () => {
    if (!user?.id || !postId) return;
    
    const result = await checkIfAlreadyReported(postId, user.id);
    if (result.success) {
      setAlreadyReported(result.alreadyReported);
    }
  };

  const onSubmit = () => {
    if (!selectedReason) {
      Alert.alert('Select a reason', 'Please choose a reason to continue.');
      return;
    }

    if (alreadyReported) {
      Alert.alert('Already Reported', 'You have already reported this post.');
      return;
    }

    setLoading(true);
    
    createReport({
      postId,
      reason: selectedReason,
      reporterId: user?.id
    }).then((result: any) => {
      setLoading(false);
      
      if (result.success) {
        Alert.alert('Thanks for your report', 'We received your report and will review it shortly.', [
          {
            text: 'OK',
            onPress: () => navigation.goBack(),
          },
        ]);
      } else {
        Alert.alert('Error', result.msg || 'Failed to submit report. Please try again.');
      }
    }).catch((error) => {
      setLoading(false);
      console.error('Error submitting report:', error);
      Alert.alert('Error', 'An unexpected error occurred. Please try again.');
    });
  };

  return (
    <ScreenWrapper>
      <View style={styles.container}>
        <Text style={styles.title}>Report Post</Text>
        <Text style={styles.subtitle}>Select a reason that best describes the issue.</Text>

        <ScrollView contentContainerStyle={styles.list} showsVerticalScrollIndicator={false}>
          {REPORT_REASONS.map((reason) => {
            const selected = selectedReason === reason;
            return (
              <TouchableOpacity
                key={reason}
                style={[styles.reasonRow, selected && styles.reasonRowSelected]}
                activeOpacity={0.8}
                onPress={() => setSelectedReason(reason)}
              >
                <View style={[styles.radioOuter, selected && styles.radioOuterSelected]}>
                  {selected && <View style={styles.radioInner} />}
                </View>
                <Text style={styles.reasonText}>{reason}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {alreadyReported ? (
          <View style={styles.alreadyReportedContainer}>
            <Text style={styles.alreadyReportedText}>You have already reported this post</Text>
          </View>
        ) : (
          <TouchableOpacity 
            style={[styles.submitBtn, (!selectedReason || loading) && { opacity: 0.6 }]} 
            onPress={onSubmit}
            disabled={!selectedReason || loading}
          >
            <Text style={styles.submitText}>
              {loading ? 'Submitting...' : 'Submit Report'}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </ScreenWrapper>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: wp(4),
    paddingTop: hp(2),
    gap: 12,
  },
  title: {
    fontSize: hp(3),
    fontWeight: '700',
    color: theme.colors.text,
  },
  subtitle: {
    fontSize: hp(1.9),
    color: theme.colors.textLight,
  },
  list: {
    paddingVertical: hp(2),
    gap: 10,
  },
  reasonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: hp(1.8),
    paddingHorizontal: wp(3),
    backgroundColor: 'white',
    borderRadius: theme.radius.md,
    ...theme.shadows.small,
  },
  reasonRowSelected: {
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  radioOuter: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: theme.colors.grayDark,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: wp(3),
  },
  radioOuterSelected: {
    borderColor: theme.colors.primary,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: theme.colors.primary,
  },
  reasonText: {
    flex: 1,
    fontSize: hp(2),
    color: theme.colors.text,
  },
  submitBtn: {
    marginTop: 'auto',
    backgroundColor: theme.colors.primary,
    paddingVertical: hp(1.8),
    borderRadius: theme.radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitText: {
    color: 'white',
    fontSize: hp(2.1),
    fontWeight: '600',
  },
  alreadyReportedContainer: {
    marginTop: 'auto',
    paddingVertical: hp(2),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.grayLight,
    borderRadius: theme.radius.lg,
  },
  alreadyReportedText: {
    color: theme.colors.textLight,
    fontSize: hp(1.9),
    textAlign: 'center',
  },
});
